/**
 * Iter105: Activity-Feed-Widget für das Dashboard.
 *
 * Zeigt die letzten ~20 Aktionen (audit_log) als kompakte Timeline.
 */
import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Activity,
  FileText,
  FileInput,
  Receipt,
  BookOpen,
  Users,
  User,
  Landmark,
  Package,
  Bell,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { formatRelative } from "@/lib/formatDate";

const ICON_MAP = {
  FileText, FileInput, Receipt, BookOpen, Users, User, Landmark, Package, Bell, Activity,
};

const ACTION_COLOR = {
  create: "text-emerald-600 bg-emerald-50",
  created: "text-emerald-600 bg-emerald-50",
  update: "text-sky-600 bg-sky-50",
  updated: "text-sky-600 bg-sky-50",
  delete: "text-rose-600 bg-rose-50",
  deleted: "text-rose-600 bg-rose-50",
  post: "text-violet-600 bg-violet-50",
  posted: "text-violet-600 bg-violet-50",
  paid: "text-emerald-600 bg-emerald-50",
  bulk_delete: "text-rose-600 bg-rose-50",
  bulk_status_change: "text-amber-600 bg-amber-50",
};

export function ActivityFeedWidget({ companyId, limit = 15 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(
        `/companies/${companyId}/activity-feed`,
        { params: { limit } }
      );
      setItems(r.data?.items || []);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [companyId, limit]);

  return (
    <Card
      className="p-4 border border-slate-200 bg-white"
      data-testid="activity-feed-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-start gap-2">
          <Activity className="h-4 w-4 text-violet-700 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-slate-900">
              Aktivitäten
            </div>
            <div className="text-[11px] text-slate-500">
              Was zuletzt passiert ist
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={load}
          disabled={loading}
          data-testid="activity-feed-refresh"
          title="Aktualisieren"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>

      {items.length === 0 && !loading && (
        <div
          className="text-center py-6 text-xs text-slate-400"
          data-testid="activity-feed-empty"
        >
          Noch keine Aktivitäten erfasst.
        </div>
      )}

      <ul className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
        {items.map((it) => {
          const IconComp = ICON_MAP[it.icon] || Activity;
          const colorClass =
            ACTION_COLOR[it.action] || "text-slate-600 bg-slate-50";
          return (
            <li
              key={it.id}
              className="flex items-start gap-2.5 py-1.5 border-b border-slate-50 last:border-0"
              data-testid={`activity-item-${it.id}`}
            >
              <div
                className={`shrink-0 rounded-full p-1.5 ${colorClass}`}
              >
                <IconComp className="h-3 w-3" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-900 leading-snug">
                  {it.summary}
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-slate-500 mt-0.5">
                  <span>{formatRelative(it.timestamp)}</span>
                  {it.user_email && (
                    <>
                      <span>·</span>
                      <span className="truncate">{it.user_email}</span>
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
